
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import AuthButtons from "./AuthButtons";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { User, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useEffect, useState, useRef } from "react";

interface UserInfoProps {
  user: any;
  isAdmin: boolean;
  onSignIn: () => void;
  onSignUp: () => void;
  isLoading?: boolean;
}

export default function UserInfo({ user, isAdmin, onSignIn, onSignUp, isLoading = false }: UserInfoProps) {
  const { t } = useLanguage();
  const hasShownAdminToast = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Remove the admin verification toast - we don't want to show it anymore
  
  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      toast.success(t('auth.signedOutSuccess'));
      // Reset admin verification status on logout
      hasShownAdminToast.current = false;
      
      // Redirect to main page if on auth-protected routes
      const authProtectedRoutes = ['/profile', '/admin', '/favorites'];
      const currentPath = location.pathname;
      if (authProtectedRoutes.some(route => currentPath.startsWith(route))) {
        navigate('/');
      }
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error(t('auth.signOutError'));
    }
  };
  
  if (isLoading) {
    return (
      <div className="hidden md:flex md:items-center md:space-x-2">
        <Button variant="outline" size="sm" disabled>
          <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          Loading...
        </Button>
      </div>
    );
  }
  
  return (
    <div className="hidden md:flex md:items-center md:space-x-2">
      {user ? (
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button variant="outline" size="sm" asChild className="mr-1">
              <Link to="/admin">
                <ShieldCheck className="mr-1 h-4 w-4" />
                {t("nav.admin")}
              </Link>
            </Button>
          )}
          <Button variant="outline" size="sm" asChild className="mr-1">
            <Link to="/profile">
              <User className="mr-1 h-4 w-4" />
              {t("nav.profile")}
            </Link>
          </Button>
          <Button variant="outline" size="sm" onClick={handleSignOut}>
            {t("nav.logout")}
          </Button>
        </div>
      ) : (
        <AuthButtons 
          onSignIn={onSignIn}
          onSignUp={onSignUp}
        />
      )}
    </div>
  );
}
